import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GMB_CATEGORIES } from '../../constants/gmbCategories';

// Template 8 theme colors
const COLORS = {
  cream: '#faf9f7',
  dark: '#1a1a1a',
  gold: '#c9a227',
  border: '#1a1a1a'
};

// Function to find related categories based on keywords
const getRelatedCategories = (userCategories, maxRelated = 10) => {
  const relatedSet = new Set();
  const userCatLower = userCategories.map(c => c.toLowerCase());

  // Extract meaningful keywords from user categories (words with 4+ characters)
  const allKeywords = new Set();
  userCategories.forEach(userCat => {
    const words = userCat.toLowerCase().split(/[\s&,]+/);
    words.forEach(word => {
      // Only use meaningful keywords (4+ chars, not common words)
      if (word.length >= 4 && !['shop', 'store', 'service', 'center', 'company', 'agency'].includes(word)) {
        allKeywords.add(word);
      }
    });
  });

  // Find categories that contain any of the keywords
  if (allKeywords.size > 0) {
    GMB_CATEGORIES.forEach(cat => {
      const catLower = cat.toLowerCase();
      // Skip if it's already in user's categories
      if (userCatLower.includes(catLower)) return;

      // Check if category contains any keyword
      for (const keyword of allKeywords) {
        if (catLower.includes(keyword)) {
          relatedSet.add(cat);
          break;
        }
      }
    });
  }

  return Array.from(relatedSet).slice(0, maxRelated);
};

const SectionContainer = styled(Box)({
  backgroundColor: COLORS.cream,
  borderBottom: `1px solid ${COLORS.dark}`
});

const SectionHeader = styled(Box)({
  padding: '48px 64px 32px',
  borderBottom: `1px solid ${COLORS.dark}`,
  '@media (max-width: 768px)': {
    padding: '32px 20px 24px'
  }
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '42px',
  fontWeight: 700,
  color: COLORS.dark,
  letterSpacing: '-1px',
  '@media (max-width: 768px)': {
    fontSize: '32px'
  }
});

const SectionSubtitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: COLORS.dark,
  opacity: 0.6,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginTop: '8px'
});

const ContentWrapper = styled(Box)({
  padding: '48px 64px',
  '@media (max-width: 768px)': {
    padding: '32px 20px'
  }
});

const CategorySection = styled(Box)({
  marginBottom: '32px',
  '&:last-child': {
    marginBottom: 0
  }
});

const CategoryLabel = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: COLORS.dark,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginBottom: '16px',
  opacity: 0.7
});

const TagsWrapper = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
  justifyContent: 'flex-start'
});

const Tag = styled(Box)({
  padding: '12px 24px',
  border: `1px solid ${COLORS.dark}`,
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  color: COLORS.dark,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  transition: 'all 0.2s ease',
  cursor: 'default',
  '&:hover': {
    backgroundColor: COLORS.dark,
    color: COLORS.cream
  }
});

const CategoryTag = styled(Tag)({
  backgroundColor: `${COLORS.gold}15`,
  borderColor: COLORS.gold,
  color: COLORS.gold,
  '&:hover': {
    backgroundColor: COLORS.gold,
    color: COLORS.cream
  }
});

const ServiceTag = styled(Tag)({
  backgroundColor: `${COLORS.dark}08`,
  '&:hover': {
    backgroundColor: COLORS.dark,
    color: COLORS.cream
  }
});

const AttributeTag = styled(Tag)({
  border: `1px dashed ${COLORS.dark}`,
  opacity: 0.8,
  '&:hover': {
    opacity: 1,
    backgroundColor: COLORS.dark,
    color: COLORS.cream,
    borderStyle: 'solid'
  }
});

const CategoryTagsSection = ({ businessInfo, categories, services, products, keywordsData }) => {
  // Collect GMB profile categories
  const gmbCategories = [];

  // Add primary category
  if (businessInfo?.category) {
    gmbCategories.push(businessInfo.category);
  }

  // Add categories array
  if (Array.isArray(categories)) {
    categories.forEach(cat => {
      if (typeof cat === 'string' && !gmbCategories.includes(cat)) {
        gmbCategories.push(cat);
      } else if ((cat?.name || cat?.displayName) && !gmbCategories.includes(cat.name || cat.displayName)) {
        gmbCategories.push(cat.name || cat.displayName);
      }
    });
  }

  // Get related categories based on keywords
  const relatedCategories = getRelatedCategories(gmbCategories, 10);

  // Combine all categories into one array (user's categories first, then related)
  const allCategories = [...gmbCategories, ...relatedCategories];

  // Collect tags from keywords data (from Performance page), services, and products
  const allTags = new Set();

  // Add keywords from keywordsData (Performance page keywords)
  // Keywords have structure: { searchTerm: string, searchVolume: number, ... }
  if (Array.isArray(keywordsData) && keywordsData.length > 0) {
    keywordsData.forEach(keyword => {
      if (keyword?.searchTerm) {
        allTags.add(keyword.searchTerm);
      } else if (typeof keyword === 'string') {
        allTags.add(keyword);
      }
    });
  }

  // Add services - only if user has real services (not mock/placeholder)
  // Check if services have actual user data (id from user input, not mock data)
  if (Array.isArray(services) && services.length > 0) {
    services.forEach(service => {
      // Only add if it's real user data (has id or has actual name)
      const hasRealData = service?.id || (service?.name && service.name !== 'placeholder');
      if (hasRealData) {
        if (service?.name) allTags.add(service.name);
        else if (service?.title) allTags.add(service.title);
      }
    });
  }

  // Add products - only if user has real products (not mock/placeholder)
  if (Array.isArray(products) && products.length > 0) {
    products.forEach(product => {
      // Only add if it's real user data
      const hasRealData = product?.id || (product?.name && product.name !== 'placeholder');
      if (hasRealData) {
        if (product?.name) allTags.add(product.name);
        else if (product?.title) allTags.add(product.title);
      }
    });
  }

  const tagsArray = Array.from(allTags).slice(0, 15);

  // Collect attribute tags
  const attributeTags = [];
  if (businessInfo?.hasWifi) attributeTags.push('WiFi Available');
  if (businessInfo?.petFriendly) attributeTags.push('Pet Friendly');
  if (businessInfo?.outdoorSeating) attributeTags.push('Outdoor Seating');
  if (businessInfo?.parking) attributeTags.push('Parking Available');
  if (businessInfo?.wheelchairAccessible) attributeTags.push('Wheelchair Accessible');

  const hasCategories = allCategories.length > 0;
  const hasTags = tagsArray.length > 0;
  const hasAttributes = attributeTags.length > 0;

  if (!hasCategories && !hasTags && !hasAttributes) {
    return null;
  }

  return (
    <SectionContainer id="tags">
      <SectionHeader>
        <SectionSubtitle>Discover More</SectionSubtitle>
        <SectionTitle>Tags & Categories</SectionTitle>
      </SectionHeader>

      <ContentWrapper>
        {hasCategories && (
          <CategorySection>
            <CategoryLabel>Categories</CategoryLabel>
            <TagsWrapper>
              {allCategories.map((cat, index) => (
                <CategoryTag key={`cat-${index}`}>{cat}</CategoryTag>
              ))}
            </TagsWrapper>
          </CategorySection>
        )}

        {hasTags && (
          <CategorySection>
            <CategoryLabel>Tags</CategoryLabel>
            <TagsWrapper>
              {tagsArray.map((tag, index) => (
                <ServiceTag key={`tag-${index}`}>{tag}</ServiceTag>
              ))}
            </TagsWrapper>
          </CategorySection>
        )}

        {hasAttributes && (
          <CategorySection>
            <CategoryLabel>Features & Amenities</CategoryLabel>
            <TagsWrapper>
              {attributeTags.map((attr, index) => (
                <AttributeTag key={`attr-${index}`}>{attr}</AttributeTag>
              ))}
            </TagsWrapper>
          </CategorySection>
        )}
      </ContentWrapper>
    </SectionContainer>
  );
};

export default CategoryTagsSection;
